import classNames from 'classnames';
import { isToday, isYesterday } from 'date-fns';
import React, { useCallback, useMemo } from 'react';

import { convertDataToPeriods } from 'common/components/GroupedList/helpers';

import { useI18n } from 'common/hooks';

import { memoizedComponent } from 'utils/memoizeComponent';

import './GroupedList.css';
import GroupedListRowComponent from './GroupedListRow.component';

interface GroupedListProps<T> {
  pattern?: string;
  filter?: { key: keyof T; value: boolean | string | number };
  data?: T[];
  groupKey: keyof T & string;
  row?: boolean;
  column?: boolean;
  'data-test'?: string;
  children: (data: T, isTest?: boolean) => React.ReactElement<T>;
  listClassNames?: string;
}

const GroupedListNew = <T,>({
  children,
  groupKey,
  data = [],
  filter,
  row,
  column = true,
  pattern = 'MMMM d, yyyy',
  'data-test': dataAttr = '',
  listClassNames = '',
}: GroupedListProps<T>) => {
  type RowType = T | string;

  const { translate } = useI18n();

  const filteredData = useMemo(() => {
    if (!filter) return data;

    return data.filter((i) => i[filter.key] === filter.value);
  }, [filter, data]);

  const periods = useMemo(
    () => convertDataToPeriods<T>(filteredData || [], pattern, groupKey),
    [filteredData, groupKey, pattern],
  );

  const sortByDate = useCallback((a: string, b: string) => +new Date(b) - +new Date(a), []);
  const sortDateByKey = useCallback(
    (a: T, b: T) => +new Date(String(b[groupKey])) - +new Date(String(a[groupKey])),
    [groupKey],
  );

  const mappedData: RowType[] = useMemo(
    () =>
      Object.keys(periods)
        .sort(sortByDate)
        .map((period) => {
          if (row) return [...periods[period].sort(sortDateByKey)];
          if (column) return [period, ...periods[period].sort(sortDateByKey)];
          return [...periods[period]];
        })
        .flat(),
    [column, periods, row, sortByDate, sortDateByKey],
  );

  const renderPeriodHeadline = useCallback(
    (period: string) => {
      if (isToday(new Date(String(period)))) return translate.toText('mobile:headlines:today');
      if (isYesterday(new Date(String(period)))) return translate.toText('mobile:headlines:yesterday');
      return period;
    },
    [translate],
  );

  const renderChildren = useCallback((rowData: T) => children(rowData), [children]);

  const renderRow = useCallback(
    (rowData: RowType, index: number) => (
      <GroupedListRowComponent key={index} row={row} column={column}>
        {typeof rowData === 'string' ? renderPeriodHeadline(rowData) : renderChildren(rowData)}
      </GroupedListRowComponent>
    ),
    [column, renderChildren, renderPeriodHeadline, row],
  );

  const renderNodes = useMemo(() => mappedData.map(renderRow), [mappedData, renderRow]);

  const groupedListClassName = classNames({
    'Body3_reg_16 grouped-list': true,
    [listClassNames]: listClassNames,
  });

  return (
    <div data-test={dataAttr} className={groupedListClassName}>
      {renderNodes}
    </div>
  );
};
const MemoizedGroupedListNew = memoizedComponent(GroupedListNew);

export default MemoizedGroupedListNew;